(function () {
  const WRAPPER_SELECTOR =
    'kickflip-customizer-iframe, .kickflip-customizer-iframe, #kickflip-customizer-iframe'
  const TRIGGER_SELECTOR =
    '#product-mczr-modal-button, #kickflip-customize-button'

  let lastTrigger = null
  let activeWrapper = null
  let inertRoots = []
  let hasMovedFocus = false
  let focusAttempts = 0

  const dialogLabel = () =>
    window.theme?.strings?.customiserDialog || 'Product customizer'

  const isVisible = (element) => {
    if (!element) return false
    if (element.hasAttribute('hidden')) return false
    if (element.getAttribute('aria-hidden') === 'true') return false

    const style = window.getComputedStyle(element)
    if (style.display === 'none' || style.visibility === 'hidden') return false

    const rect = element.getBoundingClientRect()
    return rect.width > 0 && rect.height > 0
  }

  const getWrapper = () => document.querySelector(WRAPPER_SELECTOR)

  const getIframe = (wrapper) => {
    if (!wrapper) return null
    if (wrapper.tagName === 'IFRAME') return wrapper

    const lightDomIframe = wrapper.querySelector('iframe')
    if (lightDomIframe) return lightDomIframe

    return wrapper.shadowRoot?.querySelector('iframe') || null
  }

  const getDialog = (wrapper) =>
    wrapper.closest('[role="dialog"], [aria-modal="true"]') || wrapper

  const isIframeOrInsideIframe = (element) => {
    if (!element || !activeWrapper) return false
    if (element.tagName === 'IFRAME') return true
    if (element === getIframe(activeWrapper)) return true
    // Focus inside a shadow iframe is retargeted to the host element.
    if (element === activeWrapper && getIframe(activeWrapper)) return true
    return false
  }

  const getBodyRoot = (element) => {
    let root = element
    while (root.parentElement && root.parentElement !== document.body) {
      root = root.parentElement
    }
    return root
  }

  const setBackgroundInert = (dialog, enabled) => {
    inertRoots.forEach((root) => root.removeAttribute('inert'))
    inertRoots = []

    if (!enabled) return

    const dialogRoot = getBodyRoot(dialog);

    [...document.body.children].forEach((child) => {
      if (child === dialogRoot) return
      child.setAttribute('inert', '')
      inertRoots.push(child)
    })
  }

  const decorateDialog = (wrapper, iframe) => {
    const dialog = getDialog(wrapper)

    if (!dialog.hasAttribute('role')) dialog.setAttribute('role', 'dialog')
    dialog.setAttribute('aria-modal', 'true')
    if (
      !dialog.getAttribute('aria-label') &&
      !dialog.getAttribute('aria-labelledby')
    ) {
      dialog.setAttribute('aria-label', dialogLabel())
    }

    if (iframe && !iframe.getAttribute('title')) {
      iframe.setAttribute('title', dialogLabel())
    }

    return dialog
  }

  const moveFocusIntoIframe = (wrapper) => {
    const iframe = getIframe(wrapper)
    if (!iframe) return false

    iframe.setAttribute('tabindex', '0')

    try {
      iframe.contentWindow?.focus()
    } catch (error) {}

    iframe.focus()

    return (
      document.activeElement === iframe || document.activeElement === wrapper
    )
  }

  const focusIframeWhenReady = (wrapper) => {
    focusAttempts = 0

    const tryFocus = () => {
      focusAttempts += 1
      if (activeWrapper !== wrapper) return

      if (moveFocusIntoIframe(wrapper)) {
        hasMovedFocus = true
        return
      }

      if (focusAttempts < 30) window.setTimeout(tryFocus, 100)
    }

    tryFocus()
  }

  const openCustomizer = (wrapper) => {
    const iframe = getIframe(wrapper)
    const isNewSession = activeWrapper !== wrapper

    if (isNewSession) {
      lastTrigger = lastTrigger || document.activeElement
      activeWrapper = wrapper
      hasMovedFocus = false

      const dialog = decorateDialog(wrapper, iframe)
      setBackgroundInert(dialog, true)
      focusIframeWhenReady(wrapper)
    } else if (iframe && !hasMovedFocus) {
      decorateDialog(wrapper, iframe)
      focusIframeWhenReady(wrapper)
    }

    if (iframe && !iframe.dataset.focusBound) {
      iframe.dataset.focusBound = 'true'
      iframe.addEventListener('load', () => {
        if (activeWrapper === wrapper) moveFocusIntoIframe(wrapper)
      })
    }
  }

  const closeCustomizer = () => {
    if (!activeWrapper) return

    setBackgroundInert(activeWrapper, false)
    activeWrapper = null
    hasMovedFocus = false
    focusAttempts = 0

    if (lastTrigger && typeof lastTrigger.focus === 'function') {
      lastTrigger.focus()
    }
  }

  const syncCustomizerState = () => {
    const wrapper = getWrapper()

    if (wrapper && isVisible(wrapper)) {
      openCustomizer(wrapper)
      return
    }

    if (activeWrapper) closeCustomizer()
  }

  const onFocusIn = (event) => {
    if (!activeWrapper) return

    const dialog = getDialog(activeWrapper)
    if (
      dialog.contains(event.target) ||
      activeWrapper.shadowRoot?.contains(event.target)
    ) {
      return
    }

    moveFocusIntoIframe(activeWrapper)
  }

  const onKeyDown = (event) => {
    if (!activeWrapper) return

    if (event.key === 'Escape') {
      const dialog = getDialog(activeWrapper)
      const closeButton = dialog.querySelector(
        'button[aria-label*="close" i], button[aria-label*="Close"], [class*="close" i]',
      )

      if (closeButton) closeButton.click()
      return
    }

    // Let Tab enter and move around inside the iframe. Only pull
    // focus back if it would leave the dialog entirely.
    if (event.key !== 'Tab') return
    if (isIframeOrInsideIframe(document.activeElement)) return

    const iframe = getIframe(activeWrapper)
    if (!iframe) return

    event.preventDefault()
    moveFocusIntoIframe(activeWrapper)
  }

  const rememberTrigger = (event) => {
    const trigger = event.target.closest(TRIGGER_SELECTOR)
    if (trigger) lastTrigger = trigger
  }

  document.addEventListener('focusin', onFocusIn, true)
  document.addEventListener('keydown', onKeyDown, true)
  document.addEventListener('click', rememberTrigger, true)

  const observer = new MutationObserver(syncCustomizerState)
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['hidden', 'style', 'class', 'aria-hidden', 'open'],
  })

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncCustomizerState)
  } else {
    syncCustomizerState()
  }
})()
